import { useState } from 'react';
import type { StockRow } from '../api/planner';
import { UNIT_GROUPS, isKnownUnit, normalizeUnit, unitLabel } from '../units';

/** Today, as the value a date input wants. */
function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/** What the pantry needs to know about one container, asked once.
 *
 * Two screens ask it. The shopping list asks about a jar you noticed in the
 * cupboard while reading the list; the pantry page asks about a jar you are
 * holding. The questions are identical because the answer is the same kind of
 * fact either way, and the reason this is one component rather than two is
 * that a pantry which asks them differently in two places starts recording
 * them differently too.
 *
 * Every field is optional. Bought today, unopened, however much the shop sells
 * it in is a real answer and the commonest one, which is why it is what the
 * form already says.
 *
 * The fields are held here rather than by the caller, so a caller with more
 * than one thing to add resets by remounting with a fresh `key` instead of
 * clearing seven pieces of state and forgetting one.
 */
export default function StockFields({
  name,
  defaultUnit,
  packHint,
  pending,
  error,
  submitLabel = 'Add to pantry',
  onSubmit,
}: {
  /** The ingredient being described, for the labels a screen reader reads. */
  name: string;
  /** What to start the unit box on — the shop's unit, or the corpus's. */
  defaultUnit: string;
  /** What "leave the amount blank" means here: a package, or just a whole one. */
  packHint: string;
  pending: boolean;
  error?: string;
  submitLabel?: string;
  onSubmit: (row: Omit<StockRow, 'ingredient_id'>) => void;
}) {
  const [quantity, setQuantity] = useState('');
  // Fold "tablespoon" onto "tbsp" so it selects an option rather than looking
  // unrecognised; anything genuinely outside the vocabulary is kept verbatim
  // and offered as its own option, because quietly rewriting somebody's unit
  // is worse than showing one the dropdown does not otherwise list.
  const [unit, setUnit] = useState(
    isKnownUnit(defaultUnit) ? normalizeUnit(defaultUnit) : defaultUnit,
  );
  const unknownUnit = isKnownUnit(defaultUnit) ? '' : defaultUnit;
  const [opened, setOpened] = useState(false);
  const [frozen, setFrozen] = useState(false);
  const [openedDate, setOpenedDate] = useState(today());
  const [purchased, setPurchased] = useState(today());
  const [expires, setExpires] = useState('');

  function submit() {
    onSubmit({
      quantity: quantity.trim() || undefined,
      unit: unit.trim() || undefined,
      purchased_date: purchased || undefined,
      opened_date: opened ? openedDate : undefined,
      expiration_date: expires || undefined,
      frozen: frozen || undefined,
    });
  }

  return (
    <div className="stock__panel">
      <div className="stock__row">
        <label className="stock__field">
          <span>How much is left</span>
          <input
            inputMode="decimal"
            value={quantity}
            placeholder="e.g. 1.5"
            aria-label={`How much ${name} is left`}
            onChange={(event) => setQuantity(event.target.value)}
          />
        </label>
        <label className="stock__field stock__field--narrow">
          <span>Unit</span>
          <select
            value={unit}
            aria-label={`What the ${name} is measured in`}
            onChange={(event) => setUnit(event.target.value)}
          >
            {unknownUnit && <option value={unknownUnit}>{unknownUnit}</option>}
            {UNIT_GROUPS.map((group) => (
              <optgroup key={group.label} label={group.label}>
                {group.units.map((value) => (
                  <option key={`${group.label}-${value}`} value={value}>
                    {unitLabel(value)}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </label>
      </div>
      {/* The amount used to say this by being pre-filled with the pack
          size, which read as an instruction to type "16 oz" into a number
          box. It is a sentence, so say it in one. */}
      <p className="muted small">{packHint}</p>
      <label className="stock__check">
        <input
          type="checkbox"
          checked={opened}
          onChange={(event) => setOpened(event.target.checked)}
        />
        <span>Already opened</span>
      </label>
      {/* The freezer is a different shelf life, not a longer one, and
          nothing else on this form can express that: a bag of chicken
          breasts dated as fridge stock is a day old on arrival. */}
      <label className="stock__check">
        <input
          type="checkbox"
          checked={frozen}
          onChange={(event) => setFrozen(event.target.checked)}
        />
        <span>In the freezer</span>
      </label>

      <div className="stock__row">
        <label className="stock__field">
          <span>{opened ? 'Opened' : 'Bought'}</span>
          <input
            type="date"
            value={opened ? openedDate : purchased}
            aria-label={
              opened ? `When the ${name} was opened` : `When the ${name} was bought`
            }
            onChange={(event) =>
              opened
                ? setOpenedDate(event.target.value)
                : setPurchased(event.target.value)
            }
          />
        </label>
        <label className="stock__field">
          <span>Use by</span>
          <input
            type="date"
            value={expires}
            aria-label={`The date printed on the ${name}`}
            onChange={(event) => setExpires(event.target.value)}
          />
        </label>
      </div>
      {/* One line, because this panel is read in a shop. What each blank
          means is worth saying once; saying it three times is a wall of
          text between you and a button. */}
      <p className="muted small">
        Blank amount means a whole one. Blank use-by is worked out from {name}'s
        {frozen ? ' freezer life' : ' shelf life'} and the date above.
      </p>

      <div className="stock__actions">
        <button
          type="button"
          className="btn btn--small btn--primary"
          disabled={pending}
          onClick={submit}
        >
          {pending ? 'Adding…' : submitLabel}
        </button>
        {error && <span className="finder__error">{error}</span>}
      </div>
    </div>
  );
}
